import { Button, ButtonEnums, FeedPlaceholder } from 'components/atoms';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown, InputProps as SemanticUIInputProps } from 'semantic-ui-react';
import styled from 'styled-components';
import { CheckoutCountriesListHash } from 'mxp-utils';
import { ExemptionPathwaySelector } from '../PageExemptionPathwaySelector';
import { fetchMembershipTypes } from 'modules/membership';
import { ExemptionLevels, MembershipTypes, Product } from 'mxp-schemas';
import { useDispatch, useSelector } from 'react-redux';
import { membershipTypesSelector, membershipOfflineExemptionsEntryLevelsSelector } from 'modules/membership/selectors';
import {
  educationExemptionLevelSelector,
  pleProfessionalBodiesSelector,
  professionalBodyExemptionLevelSelector,
  workExemptionLevelSelector,
  setOfflineExemptionStatus,
} from 'modules/personLevelExemption';
import { getExemptionLevelReferenceByName } from 'modules/exemptionReference/action';
import { fetchExemptionLevel, setMembershipEvent, setOfflineExemptionsEntryLevels } from 'modules/membership/actions';
import { getLevelNamebyNumber, getPath } from 'utils';
import { Routes } from 'constants/index';
import { useHistory } from 'react-router';

const initialData = {
  id: '',
  name: '',
  title: '',
  description: '',
  exemptionLevelKey: '',
  lowerExemptionLevelRef: [],
  masterExamReference: [],
  highestExemptionReference: {},
};

export const ExemptionCalculatorSummary: React.FC = () => {
  const dispatch = useDispatch();
  const [country, setCountry] = useState<string>('');
  const [pathway, setPathway] = useState('');

  const [exemptionInfoList, setExemptionInfoList] = useState({
    [MembershipTypes.CIMALearningPathwaySlug.CIMA_PQ as string]: initialData,
    [MembershipTypes.CIMALearningPathwaySlug.FLP as string]: initialData,
    [MembershipTypes.CIMALearningPathwaySlug.APPRENTICESHIP as string]: initialData,
  });

  const history = useHistory();
  const { list } = useSelector(membershipTypesSelector);
  const offlineExemptionsEntryLevels = useSelector(membershipOfflineExemptionsEntryLevelsSelector);
  const workExemptionLevel = useSelector(workExemptionLevelSelector);
  const professionalBodyExemptionLevel = useSelector(professionalBodyExemptionLevelSelector);
  const educationExemptionLevel = useSelector(educationExemptionLevelSelector);
  const pleProfBodies = useSelector(pleProfessionalBodiesSelector);
  const profBodies = pleProfBodies?.filter(
    (item: any) => (item !== null && item?.profBodyName) || item?.selectedPassedUSCPAExam
  );
  const isCPALicensed =
    profBodies.length === 1 &&
    profBodies[0].selectedPassedUSCPAExam === ExemptionLevels.USCpaExamEnum.YES_LICENSED_BEFORE;

  const [isPathwayDataLoading, setIsPathwayDataLoading] = useState(false);

  const countriesOptions = React.useMemo(() => {
    const countryList = Object?.values(CheckoutCountriesListHash)?.map((data: any) => ({
      key: data?.key,
      text: data?.text,
      value: data?.ISOAlpha3Code,
    }));
    return countryList || [];
  }, []);

  const handleSelectCountry = useCallback((e: any, data: SemanticUIInputProps) => {
    setCountry(data.value);
  }, []);

  const handleSelectPathway = useCallback(
    (pathwayValue: string) => {
      setPathway(pathwayValue);
    },
    [setPathway]
  );

  const isUnitedKingdom = country === CheckoutCountriesListHash?.GBR.ISOAlpha3Code;
  const isAmerica = Boolean(country && CheckoutCountriesListHash?.[country]?.isAmerica);

  const productList = useMemo(
    () => {
      const typeList = list?.filter(
        (product: any) => product?.slug !== MembershipTypes.CIMALearningPathwaySlug.CHINESE_PQ
      );
      // United Kingdom - Apprenticeship will appear in UK only
      if (isUnitedKingdom) {
        return typeList;
      }
      // America - only FLP will appear
      if (isAmerica) {
        const americaProducts = list?.filter(product => product.slug === MembershipTypes.CIMALearningPathwaySlug.FLP);
        return americaProducts;
      }
      // Others - Apprenticeship will not appear outside UK
      if (!isUnitedKingdom && !isAmerica) {
        const othersProducts = typeList?.filter(
          product => product.slug !== MembershipTypes.CIMALearningPathwaySlug.APPRENTICESHIP
        );
        return othersProducts;
      }

      return typeList;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, isUnitedKingdom, isAmerica]
  );

  const handleBeginApplicationClick = () => {
    dispatch(setMembershipEvent('isBeginApplicationClicked', true));
    dispatch(setOfflineExemptionStatus(true));
    history.push(getPath(Routes.EXEMPTION_CALCULATOR_RESULT));
  };

  const handleSaveDataClick = () => {
    dispatch(setOfflineExemptionStatus(true));
    history.push(getPath(Routes.EXEMPTION_CALCULATOR_RESULT));
  };

  useEffect(() => {
    dispatch(fetchMembershipTypes(Product.MembershipApplicationType.CIMA));
    dispatch(setOfflineExemptionsEntryLevels([MembershipTypes.Pathway.PQ, MembershipTypes.Pathway.FLP]));
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    const highestLevelExemption = String(
      Math.max(workExemptionLevel, professionalBodyExemptionLevel, educationExemptionLevel)
    );
    const getHighestLevelExemption = async () => {
      setIsPathwayDataLoading(true);
      try {
        const flpLevel = String(getLevelNamebyNumber(Number(highestLevelExemption), true, isCPALicensed));
        const exemption = await dispatch(getExemptionLevelReferenceByName(highestLevelExemption));
        const flPexemption = await dispatch(getExemptionLevelReferenceByName(flpLevel));

        // Create an array of promises for the fetch operations
        const exemptionLevelCompoundRequest = [
          dispatch(
            fetchExemptionLevel(
              flPexemption?.payload?.getExemptionLevelReferenceByName?.Id,
              MembershipTypes.Pathway.FLP,
              null
            )
          ),
          dispatch(
            fetchExemptionLevel(
              exemption?.payload?.getExemptionLevelReferenceByName?.Id,
              MembershipTypes.Pathway.PQ,
              null
            )
          ),
          dispatch(
            fetchExemptionLevel(
              exemption?.payload?.getExemptionLevelReferenceByName?.Id,
              MembershipTypes.Pathway.APPRENTICE_L7,
              null
            )
          ),
        ];

        // Execute all fetch operations concurrently and wait for all of them to complete
        const [flpResult, pqResult, l7Result] = await Promise.all(exemptionLevelCompoundRequest);

        // Check if all fetches were successful
        if (isMounted) {
          if (
            flpResult.payload?.getExemptionLevelReferenceById?.id &&
            pqResult.payload?.getExemptionLevelReferenceById?.id &&
            l7Result.payload?.getExemptionLevelReferenceById?.id
          ) {
            setExemptionInfoList({
              [MembershipTypes.CIMALearningPathwaySlug.FLP]: flpResult.payload.getExemptionLevelReferenceById,
              [MembershipTypes.CIMALearningPathwaySlug.CIMA_PQ]: pqResult.payload.getExemptionLevelReferenceById,
              [MembershipTypes.CIMALearningPathwaySlug.APPRENTICESHIP]: l7Result.payload.getExemptionLevelReferenceById,
            });
          }
        }
      } finally {
        setIsPathwayDataLoading(false);
      }
    };

    if (isMounted) {
      getHighestLevelExemption();
    }

    return () => {
      isMounted = false;
    };
  }, [workExemptionLevel, professionalBodyExemptionLevel, educationExemptionLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledContainer>
      <StyledBreak />
      <StyledDropdownContainer>
        <StyledPageTitleS>Select your location to see the pathway available</StyledPageTitleS>
        <StyledDropdown
          name="country"
          fluid
          search
          clearable
          selection
          minCharacters={1}
          placeholder="Select your location"
          value={country}
          options={countriesOptions}
          id="country"
          data-testid="country"
          onChange={handleSelectCountry}
        />
      </StyledDropdownContainer>
      <StyledCardContainer>
        {!!country &&
          (!!productList?.length && !isPathwayDataLoading ? (
            <>
              <StyledDivWithMargin>
                <StyledPageTitleL>Select a pathway to see your exemptions based on it</StyledPageTitleL>
                <StyledPageTitleXS>
                  Individuals who reside in the Americas can only select the CGMA Finance Leadership Program (FLP). The
                  Self-tailored Learning Pathway is not currently available, and CIMA’s Apprenticeships are only
                  available in England.
                </StyledPageTitleXS>
                <StyledPageTitleXS>
                  If you have selected a location outside of the UK, you will not be eligible for Apprenticeship Pathway
                </StyledPageTitleXS>
              </StyledDivWithMargin>
              <ExemptionPathwaySelector
                productList={productList}
                pathway={pathway}
                selectPathway={handleSelectPathway}
                pathwayExemptions={exemptionInfoList}
                offlineExemptionsEntryLevels={offlineExemptionsEntryLevels}
              />
              <StyledDivWithMargin>
                <StyledPageTitleXS>
                  {' '}
                  You may notice that the starting point for the CGMA FLP differs slightly from the Self-tailored
                  Learning Pathway (previously known as CIMA PQ) and CIMA’s Apprenticeships. The difference: The CGMA
                  FLP does not include objective tests that you must schedule; assessments are embedded in the
                  coursework.
                </StyledPageTitleXS>
              </StyledDivWithMargin>

              {!!pathway && (
                <>
                  <StyledButtonContainer>
                    <StyledButton
                      className="confirm-button"
                      size={ButtonEnums.sizes.small}
                      variant={ButtonEnums.variants.primary}
                      testId="confirm"
                      onClick={handleSaveDataClick}
                      type="button"
                    >
                      Save Data
                    </StyledButton>
                    <StyledButton
                      className="begin-button"
                      size={ButtonEnums.sizes.small}
                      variant={ButtonEnums.variants.primary}
                      testId="begin"
                      onClick={handleBeginApplicationClick}
                      type="button"
                    >
                      Begin Application
                    </StyledButton>
                  </StyledButtonContainer>
                  <StyledContainer>Save your data or begin application now</StyledContainer>
                </>
              )}
            </>
          ) : (
            <FeedPlaceholder />
          ))}
      </StyledCardContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  box-sizing: border-box;
  padding: ${props => props.theme.pxToRem(5)} ${props => props.theme.pxToRem(10)};
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyledDropdownContainer = styled.div`
  background-color: white;
  padding: 20px 50px;
  border-radius: 1rem;
  width: 100%;
  box-shadow: 0px 0.125rem 0.875rem;
`;

const StyledDivWithMargin = styled.div`
  margin-top: ${props => props.theme.pxToRem(25)};
  margin-left: ${props => props.theme.pxToRem(25)};
  margin-right: ${props => props.theme.pxToRem(25)};
  margin-bottom: ${props => props.theme.pxToRem(25)};
`;

const StyledDropdown = styled(Dropdown)`
  &&&& {
    padding: ${props => props.theme.pxToRem(9)} ${props => props.theme.pxToRem(15)};
    border: solid ${props => props.theme.pxToRem(1)} ${props => props.theme.colors.neutralGrey4};
    color: ${props => props.theme.colors.neutralGrey8};
    line-height: 1.5;
    &.ui.selection.active.dropdown {
      border-color: ${props => props.theme.colors.primaryPurple};

      .menu {
        border-color: ${props => props.theme.colors.primaryPurple};
      }
    }
  }
`;

const StyledCardContainer = styled.div`
  margin-top: ${props => props.theme.pxToRem(25)};
`;

const StyledBreak = styled.div`
  margin-top: ${props => props.theme.pxToRem(12)};
`;

const StyledPageTitleL = styled.p`
  font-size: ${props => props.theme.fontSizes.l};
  line-height: 1;
  color: ${props => props.theme.colors.neutralGrey8};
  font-weight: ${props => props.theme.fontWeights.bold};

  ${props => props.theme.mediaQueries.mobileOnly} {
    font-size: ${props => props.theme.fontSizes.m};
  }
`;

const StyledPageTitleS = styled.p`
  font-size: ${props => props.theme.fontSizes.s};
  line-height: 1;
  color: ${props => props.theme.colors.neutralGrey8};
  font-weight: ${props => props.theme.fontWeights.light};

  ${props => props.theme.mediaQueries.mobileOnly} {
    font-size: ${props => props.theme.fontSizes.m};
  }
`;

const StyledPageTitleXS = styled.p`
  font-size: ${props => props.theme.fontSizes.xs};
  line-height: 1.5;
  color: ${props => props.theme.colors.neutralGrey8};
  font-weight: ${props => props.theme.fontWeights.light};

  ${props => props.theme.mediaQueries.mobileOnly} {
    font-size: ${props => props.theme.fontSizes.m};
  }
`;

const StyledButton = styled(Button)`
  width: ${props => props.theme.pxToRem(150)};
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 2rem;
  align-items: center;
  margin: 2rem 0;
`;
